import { useState, type ReactNode } from "react";
import { useLiveEmails } from "../hooks/useLiveEmails";
import type { GraphEmail } from "../services/graph";
import { ModuleHeader } from "../../../shared/components/ModuleHeader";
import { MailIcon, PaperclipIcon } from "../../../shared/components/icons";
import styles from "./EmailModule.module.css";

type EmailCategory = "urgent" | "action" | "fyi";
type EmailFilter = "all" | "pdf" | "urgent";

const AVATAR_BUCKETS = 6;

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .substring(0, 2)
    .toUpperCase();
}

function getAvatarBucket(name: string): number {
  const seed = (name.codePointAt(0) ?? 0) + (name.codePointAt(1) ?? 0);
  return Math.abs(seed) % AVATAR_BUCKETS;
}

function formatTime(received: string): string {
  const time = new Date(received);
  const isToday = time.toDateString() === new Date().toDateString();
  if (isToday) {
    return time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return time.toLocaleDateString([], { day: "numeric", month: "short" });
}

function getCategory(email: GraphEmail): EmailCategory {
  if (email.importance === "high") return "urgent";
  if (!email.isRead) return "action";
  return "fyi";
}

function getCategoryBadgeClass(cat: EmailCategory): string {
  if (cat === "urgent") return styles.ebadgeUrg!;
  if (cat === "action") return styles.ebadgeAct!;
  return styles.ebadgeFyi!;
}

function buildEmailSubtitle(loading: boolean, count: number): string {
  if (loading) return "Loading…";
  const suffix = count === 1 ? "" : "s";
  return `${count} email${suffix}`;
}

function applyFilter(emails: GraphEmail[], filter: EmailFilter): GraphEmail[] {
  if (filter === "all") return emails;
  if (filter === "urgent") return emails.filter((e) => e.importance === "high");
  return emails.filter((e) => e.hasAttachments);
}

interface EmailModuleProps {
  readonly graphToken: string | null;
  readonly onPromptInChat: (prompt: string) => void;
  readonly onBack?: () => void;
}

export function EmailModule({ graphToken, onPromptInChat, onBack }: Readonly<EmailModuleProps>) {
  const { emails, loading, error } = useLiveEmails(graphToken);
  const [filter, setFilter] = useState<EmailFilter>("all");

  if (!graphToken) {
    return (
      <div className={styles.module}>
        <ModuleHeader
          title="Email Management"
          subtitle="Connect Microsoft 365 to view emails"
          icon={MailIcon}
          accent="email"
          onBack={onBack}
        />
        <div className={styles.empty}>Sign in to access your inbox.</div>
      </div>
    );
  }

  const filtered = applyFilter(emails, filter);
  const subtitle = buildEmailSubtitle(loading, filtered.length);

  const chip = (id: EmailFilter, label: ReactNode) => (
    <button
      key={id}
      type="button"
      className={`${styles.chip} ${filter === id ? styles.chipSel : ""}`}
      onClick={() => setFilter(id)}
    >
      {label}
    </button>
  );

  return (
    <div className={styles.module}>
      <ModuleHeader
        title="Email Management"
        subtitle={subtitle}
        icon={MailIcon}
        accent="email"
        onBack={onBack}
      />

      <div className={styles.chipRow}>
        {chip("all", "All")}
        {chip("pdf", <><PaperclipIcon /> Has attachment</>)}
        {chip("urgent", <><span className={styles.urgentDot} aria-hidden="true" /> Urgent</>)}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {!loading && filtered.length === 0 && !error && (
        <div className={styles.empty}>
          {filter === "all" ? "No recent emails." : "No matching emails."}
        </div>
      )}

      <div className={styles.list}>
        {filtered.slice(0, 20).map((email) => {
          const fromName = email.from?.emailAddress?.name || email.from?.emailAddress?.address || "Unknown";
          const cat = getCategory(email);
          const badgeClass = getCategoryBadgeClass(cat);
          const onClick = () => onPromptInChat(`Summarise this email: "${email.subject || "(no subject)"}"`);

          return (
            <button
              key={email.id}
              type="button"
              className={styles.ecard}
              onClick={onClick}
            >
              <div className={styles.ecardHd}>
                <div className={styles.ecardAv} data-color={getAvatarBucket(fromName)}>
                  {getInitials(fromName)}
                </div>
                <div className={styles.ecardFrom}>{fromName}</div>
                <div className={styles.ecardTime}>{formatTime(email.receivedDateTime)}</div>
              </div>
              <div className={styles.ecardSubj}>{email.subject || "(no subject)"}</div>
              <div className={styles.ecardFoot}>
                <span className={`${styles.ebadge} ${badgeClass}`}>{cat}</span>
                {email.hasAttachments && (
                  <span className={`${styles.ebadge} ${styles.ebadgePdf}`}><PaperclipIcon /> attachment</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
